import {
  Domaine,
  Matiere,
  Programmation,
} from '@teetsh/app/src/types/programmations';
import {
  progIdAtom,
  PROGRAMMATION_KEY,
  programmationIdAtom,
} from '@teetsh/app/src/containers/Programmations/atoms/progAtoms';
import { notNullSchoolyearSchoolAtom } from '@teetsh/app/src/containers/App/context/atom';
import { AxiosError } from 'axios';
import { atom, PrimitiveAtom, useSetAtom } from 'jotai';
import { atomsWithMutation, queryClientAtom } from 'jotai-tanstack-query';
import request from '@teetsh/app/src/utils/request';
import swap from '@teetsh/app/src/utils/arrays/swap';
import { notifyErrorAtom } from '@teetsh/app/src/containers/App/Notifications/useNotification';
import {
  addToPastAtom,
  resetFutureAtom,
} from '@teetsh/app/src/containers/Programmations/atoms/historyAtoms';
import { TFunction } from 'i18next';
import { useTranslation } from 'react-i18next';

function reorderProgrammationDomaines({
  schoolyearId,
  schoolId,
  programmationId,
  domainesIds,
}: {
  schoolyearId: string;
  schoolId: string;
  programmationId: string;
  domainesIds: string[];
}) {
  const requestURL = `/schoolyears/${schoolyearId}/schools/${schoolId}/programmations/${programmationId}/domaines/reorder`;
  return request(requestURL, {
    method: 'PATCH',
    body: {
      domainesIds,
    },
  });
}

interface Props {
  domainesIds: string[];
  matiereId: string;
}

const [, reorderDomainesProgrammationQueryAtom] = atomsWithMutation<
  Partial<Programmation>,
  AxiosError,
  Props
>(get => {
  const { schoolyearId, schoolId } = get(notNullSchoolyearSchoolAtom);
  const queryClient = get(queryClientAtom);
  const programmationId = get(progIdAtom);
  return {
    mutationFn: params =>
      reorderProgrammationDomaines({
        schoolyearId,
        schoolId,
        programmationId,
        domainesIds: params.domainesIds,
      }),
    onMutate: ({ domainesIds, matiereId }) => {
      queryClient.setQueryData<Programmation>(
        [schoolyearId, schoolId, PROGRAMMATION_KEY, programmationId],
        oldProg => {
          const newProg: Programmation = {
            ...(oldProg as Programmation),
            matieres: (oldProg?.matieres || []).map(m => {
              if (m.id === matiereId) {
                return {
                  ...m,
                  domaines: domainesIds.reduce<Domaine[]>((acc, d) => {
                    const matchingDomaine = (m.domaines || []).find(
                      dm => dm.id === d,
                    );
                    if (!matchingDomaine) {
                      return acc;
                    }
                    return [...acc, matchingDomaine];
                  }, []),
                };
              }
              return m;
            }),
          };
          return newProg;
        },
      );
    },
  };
});
export const reorderDomainesAtom = (t: TFunction<['programmation']>) =>
  atom<any, [Props], any>(null, async (_get, set, params) => {
    await set(reorderDomainesProgrammationQueryAtom, [params]);
    const result = await _get(reorderDomainesProgrammationQueryAtom);
    if (result.error) {
      set(notifyErrorAtom, {
        title: t('programmation:messages.domaine.reorderError'),
      });
      return;
    }
  });

export const reorderDomainesWithHistoryAtom = (
  t: TFunction<['programmation']>,
) =>
  atom<any, [Props], any>(null, async (_get, set, params) => {
    const programmationId = _get(programmationIdAtom);
    if (!programmationId) {
      return Promise.resolve();
    }
    const queryClient = _get(queryClientAtom);
    const { schoolyearId, schoolId } = _get(notNullSchoolyearSchoolAtom);
    const oldProgrammation = queryClient.getQueryData<Programmation>([
      schoolyearId,
      schoolId,
      PROGRAMMATION_KEY,
      programmationId,
    ]);
    await set(reorderDomainesAtom(t), params);
    if (oldProgrammation) {
      set(addToPastAtom, {
        programmationId: programmationId,
        actionHistory: {
          action: 'REORDER_DOMAINES',
          payload: {
            matiereId: params.matiereId,
            oldDomainesIds:
              (oldProgrammation?.matieres || [])
                .find(m => m.id === params.matiereId)
                ?.domaines.map(d => d.id) || [],
            newDomainesIds: params.domainesIds,
          },
        },
      });
    }

    return set(resetFutureAtom, programmationId);
  });

export const reorderSingleDomaineAtom = (t: TFunction<['programmation']>) =>
  atom<
    any,
    [
      {
        matiereAtom: PrimitiveAtom<Matiere>;
        domaineId: string;
        dir: -1 | 1;
      },
    ],
    any
  >(null, async (_get, set, { domaineId, matiereAtom, dir }) => {
    const matiere = _get(matiereAtom);
    const domaineIds = matiere?.domaines.map(d => d.id) || [];
    const newDomaineIds = swap(domaineIds, domaineIds.indexOf(domaineId), dir);
    return set(reorderDomainesWithHistoryAtom(t), {
      domainesIds: newDomaineIds,
      matiereId: matiere.id,
    });
  });

export const useReorderSingleDomaine = () => {
  const { t } = useTranslation(['programmation']);
  return useSetAtom(reorderSingleDomaineAtom(t));
};
export const useReorderDomaines = () => {
  const { t } = useTranslation(['programmation']);
  return useSetAtom(reorderDomainesWithHistoryAtom(t));
};
export const useReorderDomainesWithoutHistory = () => {
  const { t } = useTranslation(['programmation']);
  return useSetAtom(reorderDomainesAtom(t));
};
